const REFRESH_REASONS = new Set(["initial", "save", "manual", "realtime", "focus", "reconnect", "retry", "timer"]);

export function refreshReason(value) {
  return REFRESH_REASONS.has(value) ? value : "unknown";
}

export function readEfficiencyEnabled(profile) {
  const value = (profile?.feature_flags || profile?.featureFlags || {}).performance_read_efficiency;
  return value === true || value === "true" || value === 1 || value === "1";
}

export function refreshScopeKey(auth, portal = "operations") {
  if (!auth?.session || !auth?.profile || auth.demo) return "";
  const profile = auth.profile;
  const session = auth.session;
  // Tokens rotate normally. The Supabase session_id remains stable until a new
  // sign-in; use it for isolation without ever logging or retaining the JWT.
  let sessionId = session.user?.last_sign_in_at || "";
  try {
    const encoded = String(session.access_token || "").split(".")[1];
    if (encoded) sessionId = JSON.parse(atob(encoded.replace(/-/g, "+").replace(/_/g, "/"))).session_id || sessionId;
  } catch { /* A non-JWT test/local token still has the user/scope boundary. */ }
  return JSON.stringify([
    session.user?.id || profile.id, sessionId, portal, profile.id, profile.role,
    profile.customer_id || profile.customerId, profile.territory,
    profile.default_location_id || profile.defaultLocationId, profile.active,
    profile.customer_onboarding_only, readEfficiencyEnabled(profile)
  ]);
}

export function isTransientReadError(error) {
  return error?.name === "TypeError" || error?.code === "RP_REQUEST_TIMEOUT"
    || error?.status === 408 || error?.status === 429 || Number(error?.status) >= 500;
}

function cancelledError() {
  return Object.assign(new Error("Refresh cancelled because the account or access scope changed."), { name: "AbortError" });
}

/** One bounded bootstrap at a time, with versioned invalidation and awaitable saves. */
export function createRefreshCoordinator({
  read, apply, getScopeKey, isVisible = () => true, onSuccess = () => {}, onStatus = () => {},
  debounceMs = 1200, maxRetries = 2, retryBaseMs = 750, retryMaxMs = 5000,
  random = Math.random, setTimer = setTimeout, clearTimer = clearTimeout
}) {
  let scope = getScopeKey();
  let epoch = 0;
  let revision = 0;
  let completedRevision = 0;
  let flight = null;
  let timer = null;
  let waiters = [];
  let reasons = new Set();
  let disposed = false;

  const status = (state, detail = {}) => onStatus({ status: state, ...detail });
  const clearScheduled = () => {
    if (timer !== null) clearTimer(timer);
    timer = null;
  };
  const rejectWaiters = error => {
    const pending = waiters;
    waiters = [];
    pending.forEach(waiter => waiter.reject(error));
  };

  function reset(nextScope = getScopeKey()) {
    epoch += 1;
    clearScheduled();
    if (flight) flight.controller.abort();
    flight = null;
    rejectWaiters(cancelledError());
    scope = nextScope;
    revision = 0;
    completedRevision = 0;
    reasons.clear();
  }

  function checkScope() {
    const nextScope = getScopeKey();
    if (nextScope !== scope) reset(nextScope);
    return !disposed && Boolean(scope);
  }

  const hasEssentialWaiter = () => waiters.some(waiter => waiter.essential);
  function schedule(immediate = false) {
    if (!checkScope() || flight || revision <= completedRevision) return;
    if (!isVisible() && !hasEssentialWaiter()) {
      clearScheduled();
      status("deferred");
      return;
    }
    // Keep the first deadline: continuous events must not starve reconciliation.
    if (timer !== null && !immediate) return;
    clearScheduled();
    if (immediate) void run();
    else timer = setTimer(() => { timer = null; void run(); }, debounceMs);
  }

  function retryDelay(ms, signal) {
    return new Promise((resolve, reject) => {
      const abort = () => { clearTimer(id); reject(cancelledError()); };
      const id = setTimer(() => { signal.removeEventListener("abort", abort); resolve(); }, ms);
      signal.addEventListener("abort", abort, { once: true });
      if (signal.aborted) abort();
    });
  }

  async function run() {
    if (!checkScope() || flight || revision <= completedRevision) return;
    if (!isVisible() && !hasEssentialWaiter()) return;
    clearScheduled();
    const current = { epoch, revision, controller: new AbortController(), reasons: [...reasons] };
    flight = current;
    reasons.clear();
    const source = current.reasons.includes("save") ? "save" : current.reasons[0] || "manual";
    const stillCurrent = () => !disposed && current.epoch === epoch && getScopeKey() === scope;
    let succeeded = false;
    try {
      let payload;
      for (let attempt = 0; ; attempt += 1) {
        if (!stillCurrent()) throw cancelledError();
        status("loading", { source, attempt });
        try {
          payload = await read({ signal: current.controller.signal, reason: attempt ? "retry" : source, attempt });
          break;
        } catch (error) {
          if (!stillCurrent() || current.controller.signal.aborted) throw cancelledError();
          if (!isTransientReadError(error) || attempt >= maxRetries || (!isVisible() && !hasEssentialWaiter())) throw error;
          const delayMs = Math.min(retryMaxMs, retryBaseMs * (2 ** attempt)) * (0.5 + random() * 0.5);
          status("retry", { source, attempt: attempt + 1, delayMs });
          await retryDelay(delayMs, current.controller.signal);
          if (!isVisible() && !hasEssentialWaiter()) throw error;
        }
      }
      if (!stillCurrent()) throw cancelledError();
      // A background read can finish after the user starts editing. Keep the
      // invalidation pending instead of replacing data beneath an active field.
      if (!isVisible() && !hasEssentialWaiter()) {
        status("deferred", { source });
        return;
      }
      apply(payload);
      completedRevision = current.revision;
      succeeded = true;
      onSuccess(payload, { source, reasons: current.reasons });
      const settled = waiters.filter(waiter => waiter.revision <= completedRevision);
      waiters = waiters.filter(waiter => waiter.revision > completedRevision);
      settled.forEach(waiter => waiter.resolve(payload));
    } catch (error) {
      if (stillCurrent()) {
        rejectWaiters(error);
        // Remain dirty for focus/reconnect or the next explicit action, but do
        // not turn a failed read into an unbounded background retry loop.
        status("error", { source, message: error instanceof Error ? error.message : "Live data refresh failed." });
      }
    } finally {
      if (current.epoch === epoch && flight === current) {
        flight = null;
        if (!checkScope()) return;
        if (succeeded) schedule(hasEssentialWaiter());
      }
    }
  }

  function invalidate(reason = "realtime", { defer = false } = {}) {
    if (!checkScope()) return;
    revision += 1;
    reasons.add(refreshReason(reason));
    if (!defer) schedule();
  }

  function request({ reason = "manual", essential = true } = {}) {
    if (!checkScope()) return Promise.reject(cancelledError());
    reason = refreshReason(reason);
    // A save completed after an in-flight read started requires a newer
    // snapshot. Concurrent ordinary refresh callers can share that read.
    if ((reason === "save" && flight) || revision <= completedRevision) revision += 1;
    reasons.add(reason);
    const targetRevision = revision;
    const result = new Promise((resolve, reject) => {
      waiters.push({ revision: targetRevision, essential, resolve, reject });
    });
    schedule(true);
    return result;
  }

  function visibilityChanged() {
    if (!checkScope()) return;
    if (!isVisible()) {
      // Reconcile on return even if the browser suspended the socket entirely.
      invalidate("focus", { defer: true });
      clearScheduled();
    } else schedule();
  }

  return {
    request, invalidate, visibilityChanged,
    reconcile(reason = "focus") {
      if (!checkScope()) return;
      if (revision > completedRevision) { reasons.add(refreshReason(reason)); schedule(); }
    },
    reset,
    dispose() { disposed = true; reset(""); },
    snapshot() { return { inFlight: Boolean(flight), dirty: revision > completedRevision, pending: waiters.length }; }
  };
}
